import { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { client, MarketCategory, Instance } from '../../../app/services/api/orderManagementClient';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { UserContext } from '../../../components/shared/useUser';
import { Section } from '../../../components/ui/Section';
import { MATERIAL_ICON_OPTIONS, resolveMaterialIcon } from '../../orderly-for-distributors/ItemTags/components/itemTagUtils';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';

const CategoryForm = () => {
  const { t } = useTranslation('categories');
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const isEdit = Boolean(categoryId);
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { instances } = useInstanceMarket();
  const [availableCategories, setAvailableCategories] = useState<MarketCategory[]>([]);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(true);

  const companyId = searchParams.get('companyId');
  const searchTerm = searchParams.get('searchTerm');

  const [formData, setFormData] = useState<MarketCategory>({
    categoryId: 0,
    parentId: -2,
    parentName: '',
    name: '',
    companyId: 0,
    instanceName: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const fetchCategoriesForInstance = async (selectedCompanyId: number) => {
    if (!selectedCompanyId) {
      setAvailableCategories([]);
      return;
    }

    try {
      const result = await client.getMarketCategories({
        companyId: selectedCompanyId.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
        sort: '',
        direction: 'asc',
      });

      if (result.success && result.data) {
        const categories = result.data.items.filter((cat) => !isEdit || cat.categoryId !== parseInt(categoryId!, 10));
        setAvailableCategories(categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setAvailableCategories([]);
    }
  };

  const fetchCategory = async () => {
    if (!categoryId) return;

    setIsLoading(true);
    try {
      const result = await client.getMarketCategory(categoryId);
      if (result.success && result.data) {
        setFormData(result.data);
        await fetchCategoriesForInstance(result.data.companyId);
      }
    } catch (error) {
      console.error('Error fetching category:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchCategory();
    }
  }, [categoryId, isEdit]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('categoryNameRequired');
    }

    if (!formData.companyId) {
      newErrors.companyId = t('instanceRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof MarketCategory) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    const newCompanyId = value?.id || 0;
    setFormData((prev) => ({
      ...prev,
      companyId: newCompanyId,
      parentId: -2,
    }));

    if (newCompanyId) {
      fetchCategoriesForInstance(newCompanyId);
    } else {
      setAvailableCategories([]);
    }

    if (errors.companyId) {
      setErrors((prev) => ({
        ...prev,
        companyId: '',
      }));
    }
  };

  const handleParentCategoryChange = (event: React.SyntheticEvent, value: MarketCategory | null) => {
    setFormData((prev) => ({
      ...prev,
      parentId: value?.categoryId || -2,
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      if (isEdit) {
        const result = await client.updateMarketCategory(categoryId!, formData);
        if (result.success) {
          setMessage(t('procurementUpdateSuccessMessage', 'Procurement category updated successfully'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/procurement/categories${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        }
      } else {
        const result = await client.createMarketCategory(formData);
        if (result.success) {
          setMessage(t('procurementCreateSuccessMessage', 'Procurement category created successfully'));
          setIsSuccess(true);
          setShowMessage(true);
          setTimeout(() => {
            const params = getUrlParams();
            navigate(`/procurement/categories${params.toString() ? `?${params.toString()}` : ''}`);
          }, 500);
        }
      }
    } catch (error) {
      console.error('Error saving category:', error);
      setMessage(isEdit
        ? t('procurementUpdateErrorMessage', 'Failed to update procurement category')
        : t('procurementCreateErrorMessage', 'Failed to create procurement category'));
      setIsSuccess(false);
      setShowMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/procurement/categories${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const userCanSave = hasPermissionTo([isEdit ? 'CategoryUpdate' : 'CategoryCreate', 'ConfigurationAdmin']);

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{t(isEdit ? 'procurementEditCategory' : 'procurementCreateCategory', isEdit ? 'Edit Procurement Category' : 'Create Procurement Category')}</PageTitle>
        <Typography>{t('loading')}</Typography>
      </PageArea>
    );
  }

  const getSelectedParentCategory = () => {
    if (formData.parentId <= 0) return null;
    return availableCategories.find((cat) => cat.categoryId === formData.parentId) || null;
  };

  return (
    <PageArea>
      <Stack spacing={6}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 4,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <PageTitle>{t(isEdit ? 'procurementEditCategory' : 'procurementCreateCategory', isEdit ? 'Edit Procurement Category' : 'Create Procurement Category')}</PageTitle>
            <Typography
              variant="textSM"
              color="text.secondary"
              sx={{ mt: 1 }}
            >
              {t('procurementCategoryFormSubtitle', 'Manage procurement category setup, hierarchy, and icon.')}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={3}
            justifyContent="flex-end"
          >
            <Button
              variant="secondary"
              size="lg"
              onClick={handleCancel}
            >
              {t('cancel')}
            </Button>
            {userCanSave && (
              <Button
                variant="primary"
                size="lg"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? t('saving') : (isEdit ? t('update') : t('create'))}
              </Button>
            )}
          </Stack>
        </Box>

        <Section style={{ mt: 0, p: 5, pb: 0, pt: 3 }}>
          <Box sx={{ px: { xs: 4, md: 5 }, pt: { xs: 2, md: 3 }, pb: { xs: 4, md: 5 } }}>
            <Box
              sx={{ mb: 2, pb: 2 }}>
              <Typography
                variant="textMD"
                fontWeight={600}
              >
                {t('basicInformation')}
              </Typography>
            </Box>

            <Grid
              container
              spacing={4}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label={t('categoryName')}
                  placeholder={t('categoryNamePlaceholder')}
                  value={formData.name}
                  onChange={handleInputChange('name')}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <FormControl fullWidth>
                  <InputLabel shrink>{t('categoryIcon')}</InputLabel>
                  <Select
                    value={formData.iconKey ?? ''}
                    displayEmpty
                    label={t('categoryIcon')}
                    onChange={(e) => setFormData((prev) => ({ ...prev, iconKey: e.target.value }))}
                    sx={{
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '1.4375em !important',
                      },
                    }}
                    renderValue={(value) => {
                      if (!value) {
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <Typography
                              variant="textSM"
                              sx={{ lineHeight: 1.2 }}
                            >
                              {t('noIcon')}
                            </Typography>
                          </Box>
                        );
                      }

                      const option = MATERIAL_ICON_OPTIONS.find((opt) => opt.key === value);
                      const IconComponent = resolveMaterialIcon(value as string);
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconComponent sx={{ fontSize: 20 }} />
                          <Typography variant="textSM">{option?.label ?? value}</Typography>
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value="">
                      <Typography variant="textSM">{t('noIcon')}</Typography>
                    </MenuItem>
                    {MATERIAL_ICON_OPTIONS.map(({ key, label, Icon }) => (
                      <MenuItem
                        key={key}
                        value={key}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Icon sx={{ fontSize: 20 }} />
                          <Typography variant="textSM">{label}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Autocomplete
                  options={instances}
                  getOptionLabel={(option) => option.name}
                  value={instances.find((inst) => inst.id === formData.companyId) || null}
                  onChange={handleInstanceChange}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('instance')}
                      placeholder={t('instancePlaceholder')}
                      error={!!errors.companyId}
                      helperText={errors.companyId}
                      required
                    />
                  )}
                  noOptionsText={t('noInstancesFound')}
                  clearOnBlur
                  selectOnFocus
                  handleHomeEndKeys
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >
                <Autocomplete
                  options={availableCategories}
                  getOptionLabel={(option) => option.name}
                  value={getSelectedParentCategory()}
                  onChange={handleParentCategoryChange}
                  isOptionEqualToValue={(option, value) => option.categoryId === value.categoryId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('parentCategory')}
                      placeholder={t('parentCategoryPlaceholder')}
                    />
                  )}
                  noOptionsText={t('noCategoriesFoundForSelection')}
                  clearOnBlur
                  selectOnFocus
                  handleHomeEndKeys
                />
              </Grid>
            </Grid>
          </Box>
        </Section>
      </Stack>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={isSuccess ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default CategoryForm;
